/**
 * 비상벨(SOS) 화면.
 *
 *  - 큰 원형 버튼을 누르면 사이렌 상태가 켜지고 배경이 붉게 바뀝니다.
 *    (진동은 바로 동작하고, 실제 사이렌 음원은 오디오 모듈 연동 지점에 TODO 로 두었습니다.)
 *  - 아래에는 현위치 기준으로 가장 가까운 안전 시설을 API 로 받아 보여 주고,
 *    전화 버튼을 누르면 번호가 채워진 통화 화면이 열립니다.
 *
 * 하단 탭바까지 덮어야 해서 일반 화면이 아니라 Modal 로 띄웁니다.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Animated,
  BackHandler,
  Easing,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  useWindowDimensions,
  Vibration,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { SirenIcon, XIcon } from 'phosphor-react-native';
import { colors } from '../../theme/colors';
import { useCurrentLocation } from '../../location/useCurrentLocation';
import { sosApi, type SafetyFacility } from '../../api/sos';
import SafetyFacilityCard from './SafetyFacilityCard';

/** 사이렌이 울리는 동안 반복할 진동 패턴 (진동 0.6s / 쉼 0.4s) */
const VIBRATION_PATTERN = [0, 600, 400];

/**
 * 비상벨 버튼 지름 — 화면 폭에 비례합니다.
 * 급할 때 보지 않고 눌러야 하는 버튼이라 최대한 크게 잡되, 좁은 기기에서
 * 카드가 밀려나지 않도록 하한을, 태블릿에서 과하게 커지지 않도록 상한을 둡니다.
 */
const BUTTON_RATIO = 0.62;
const BUTTON_MIN = 170;
const BUTTON_MAX = 300;
/** 버튼 바깥으로 퍼지는 물결 링의 여유 폭 */
const RING_GAP = 64;

const FACILITY_LIMIT = 3;

type Props = {
  visible: boolean;
  onClose: () => void;
};

function SosScreen({ visible, onClose }: Props) {
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const buttonSize = Math.round(
    Math.min(Math.max(width * BUTTON_RATIO, BUTTON_MIN), BUTTON_MAX),
  );
  const ringSize = buttonSize + RING_GAP;
  // 측위 실패·권한 거절이면 단양읍 기본 좌표로 떨어지므로 항상 값이 있습니다.
  const { coords } = useCurrentLocation();

  const [sirenOn, setSirenOn] = useState(false);
  const [facilities, setFacilities] = useState<SafetyFacility[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** 재시도 버튼이 같은 좌표로도 다시 요청하게 만드는 카운터 */
  const [reloadKey, setReloadKey] = useState(0);

  /* ── 안전 시설 조회 ── */
  useEffect(() => {
    if (!visible) return;

    const controller = new AbortController();
    setLoading(true);
    setError(null);

    sosApi
      .safetyFacilities(
        {
          latitude: coords.lat,
          longitude: coords.lng,
          limit: FACILITY_LIMIT,
        },
        controller.signal,
      )
      .then(items => {
        if (controller.signal.aborted) {
          return;
        }
        setFacilities(items);
      })
      .catch((err: Error) => {
        if (controller.signal.aborted) {
          return;
        }
        setError(err.message || '안전 시설을 불러오지 못했어요.');
      })
      .finally(() => {
        if (controller.signal.aborted) {
          return;
        }
        setLoading(false);
      });

    return () => controller.abort();
  }, [visible, reloadKey, coords]);

  /* ── 사이렌 진동 ── */
  useEffect(() => {
    if (!sirenOn) return;
    // TODO: 음원 재생 모듈(react-native-sound 등) 연동 시 여기서 사이렌 사운드도 함께 시작
    Vibration.vibrate(VIBRATION_PATTERN, true);
    return () => Vibration.cancel();
  }, [sirenOn]);

  /** 화면을 닫을 때는 사이렌도 반드시 함께 끕니다. */
  const handleClose = useCallback(() => {
    setSirenOn(false);
    Vibration.cancel();
    onClose();
  }, [onClose]);

  // 안드로이드 하드웨어 back — 사이렌이 켜져 있으면 먼저 끄고, 그 다음에 닫습니다.
  useEffect(() => {
    if (!visible) return;
    const sub = BackHandler.addEventListener('hardwareBackPress', () => {
      if (sirenOn) {
        setSirenOn(false);
        return true;
      }
      handleClose();
      return true;
    });
    return () => sub.remove();
  }, [visible, sirenOn, handleClose]);

  const background = sirenOn ? colors.sosActiveBg : colors.sosIdleBg;

  return (
    <Modal
      visible={visible}
      animationType="slide"
      statusBarTranslucent
      onRequestClose={handleClose}>
      <View style={[styles.container, { backgroundColor: background }]}>
        {/* 헤더 — 제목은 가운데, 닫기 버튼은 우측 상단 */}
        <View style={[styles.header, { paddingTop: insets.top + 10 }]}>
          <Text style={styles.headerTitle}>비상벨</Text>
          <Pressable
            onPress={handleClose}
            style={styles.closeButton}
            hitSlop={10}
            accessibilityRole="button"
            accessibilityLabel="비상벨 닫기">
            <XIcon color={colors.inkText} size={20} weight="bold" />
          </Pressable>
        </View>

        <ScrollView
          contentContainerStyle={[
            styles.content,
            { paddingBottom: insets.bottom + 28 },
          ]}
          showsVerticalScrollIndicator={false}>
          {/* 제목·부제는 한 덩어리 — space-between 이 둘 사이를 벌리지 않게 합니다 */}
          <View style={styles.headGroup}>
            <Text style={styles.headline}>
              {sirenOn
                ? '사이렌이 울리고 있어요'
                : '버튼을 누르면 큰 소리로 사이렌이 울려요'}
            </Text>
            <Text style={styles.subhead}>
              {sirenOn
                ? '다시 누르면 꺼져요'
                : '주변에 위험을 알리고 주의를 끌 수 있어요'}
            </Text>
          </View>

          <View style={[styles.buttonArea, { height: ringSize + 32 }]}>
            <PulseRing size={ringSize} delay={0} />
            <PulseRing size={ringSize} delay={900} />
            <Pressable
              onPress={() => setSirenOn(prev => !prev)}
              style={({ pressed }) => [
                styles.sirenButton,
                {
                  width: buttonSize,
                  height: buttonSize,
                  borderRadius: buttonSize / 2,
                },
                sirenOn ? styles.sirenButtonOn : styles.sirenButtonOff,
                pressed && styles.sirenButtonPressed,
              ]}
              accessibilityRole="button"
              accessibilityState={{ selected: sirenOn }}
              accessibilityLabel={sirenOn ? '비상벨 끄기' : '비상벨 울리기'}>
              <SirenIcon
                color={sirenOn ? colors.sosActiveBg : colors.inkText}
                size={Math.round(buttonSize * 0.22)}
                weight="fill"
              />
              <Text
                style={[
                  styles.sirenLabel,
                  { fontSize: Math.min(Math.round(buttonSize * 0.1), 20) },
                  sirenOn && styles.sirenLabelOn,
                ]}>
                {sirenOn ? '벨 끄기' : '비상벨 울리기'}
              </Text>
            </Pressable>
          </View>

          <View style={styles.cardSlot}>
            <SafetyFacilityCard
              facilities={facilities}
              loading={loading}
              error={error}
              active={sirenOn}
              onRetry={() => setReloadKey(prev => prev + 1)}
            />
          </View>
        </ScrollView>
      </View>
    </Modal>
  );
}

/** 버튼 주변으로 계속 퍼져 나가는 파장 링 (delay 로 두 겹을 엇갈리게 겹칩니다) */
function PulseRing({ size, delay }: { size: number; delay: number }) {
  const progress = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const animation = Animated.loop(
      Animated.sequence([
        Animated.delay(delay),
        Animated.timing(progress, {
          toValue: 1,
          duration: 1800,
          easing: Easing.out(Easing.ease),
          useNativeDriver: true,
        }),
        // 다음 바퀴를 위해 즉시 처음으로 되돌립니다.
        Animated.timing(progress, {
          toValue: 0,
          duration: 0,
          useNativeDriver: true,
        }),
      ]),
    );
    animation.start();
    return () => animation.stop();
  }, [progress, delay]);

  return (
    <Animated.View
      pointerEvents="none"
      style={[
        styles.ring,
        {
          width: size,
          height: size,
          borderRadius: size / 2,
          opacity: progress.interpolate({
            inputRange: [0, 1],
            outputRange: [0.5, 0],
          }),
          transform: [
            {
              scale: progress.interpolate({
                inputRange: [0, 1],
                outputRange: [0.86, 1.24],
              }),
            },
          ],
        },
      ]}
    />
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
    paddingBottom: 8,
  },
  headerTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.inkText,
  },
  closeButton: {
    position: 'absolute',
    right: 16,
    bottom: 0,
    width: 38,
    height: 38,
    borderRadius: 19,
    backgroundColor: 'rgba(255,255,255,0.14)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    // 제목은 위, 버튼은 그 바로 아래, 카드는 바닥(cardSlot 의 marginTop:'auto').
    // 남는 세로 공간은 전부 버튼과 카드 사이로 갑니다.
    flexGrow: 1,
    paddingHorizontal: 20,
    paddingTop: 18,
  },
  headGroup: {
    // 제목도 auto 마진을 하나 가집니다. 버튼의 위아래 auto 와 합쳐 남는 세로
    // 공간이 3등분되어, 헤더 바로 밑에 붙지 않고 아래로 내려옵니다.
    marginTop: 'auto',
  },
  headline: {
    fontSize: 21,
    lineHeight: 29,
    fontWeight: '700',
    color: colors.inkText,
    textAlign: 'center',
  },
  subhead: {
    marginTop: 8,
    fontSize: 13,
    fontWeight: '500',
    color: colors.sosTextMuted,
    textAlign: 'center',
  },
  buttonArea: {
    alignItems: 'center',
    justifyContent: 'center',
    // 높이는 링 크기에 맞춰 화면에서 계산해 넘깁니다.
    //
    // 위아래 auto 마진으로 남는 세로 공간을 절반씩 나눠 가집니다.
    // 예전에는 cardSlot 만 marginTop:'auto' 여서 남는 공간이 전부 버튼 아래로
    // 몰렸고, 그래서 버튼이 화면 위쪽에 붙어 보였습니다.
    marginTop: 'auto',
    marginBottom: 'auto',
  },
  cardSlot: {
    // 버튼의 marginBottom:'auto' 가 카드를 아래로 밀어 줍니다.
  },
  ring: {
    position: 'absolute',
    borderWidth: 1,
    borderColor: colors.sosRing,
    backgroundColor: 'rgba(255,255,255,0.05)',
  },
  sirenButton: {
    // 지름은 화면 폭에 맞춰 계산해 넘깁니다.
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
    borderWidth: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.35,
    shadowRadius: 8,
    elevation: 12,
  },
  sirenButtonOff: {
    backgroundColor: colors.danger,
    borderColor: 'rgba(255,255,255,0.85)',
  },
  sirenButtonOn: {
    backgroundColor: colors.background,
    borderColor: 'rgba(255,255,255,0.9)',
  },
  sirenButtonPressed: {
    opacity: 0.88,
  },
  sirenLabel: {
    // 크기는 버튼 지름에 비례해 화면에서 넘깁니다.
    fontWeight: '700',
    color: colors.inkText,
  },
  sirenLabelOn: {
    color: colors.sosActiveBg,
  },
});

export default SosScreen;
